import React, { useState, useEffect } from "react";
import axios from "axios";
import { getDateFormat } from "../../helpers/Utils";
import { antdMessageSuccess, antdMessageError } from "../../helpers/Utils";
import { ButtonSecondary } from "../common";
import RangePickerComp from "../common/RangePickerComp";
import Spinner from "../common/Spinner";
import SwitchComp from "../common/SwitchComp";
import TextInputModal from "../common/TextInputModal";
import ModalSpecial from "../Layout/Modals/ModalSpecial";
import { Label } from "../globals";
import { Select } from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { IconDraggableBig } from "../Layout/Icons";
import { ButtonDelete } from "../common/Buttons";

const SystemRulesModal = ({ modalFunc, id, shops, rules = [], onSaveSuccess }) => {
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedShops, setSelectedShops] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [orderShops, setOrderShops] = useState([]);
    const [orderShopsLoading, setOrderShopsLoading] = useState(false);
    const [selectedOrderShops, setSelectedOrderShops] = useState([]);
    const [originalOrderShops, setOriginalOrderShops] = useState([]); // Track original system_rule_order_shops with their IDs
    const [commentStates, setCommentStates] = useState([]);
    const [commentStatesLoading, setCommentStatesLoading] = useState(false);
    const [blacklistParams, setBlacklistParams] = useState({
        canceled_orders_limit: 0,
        returned_orders_limit: 0,
        canceled_comment_state_ids: [],
        returned_comment_state_ids: [],
    });

    const creating = id === 0;

    const [value, setValue] = useState({
        rule_title: "",
        rule_code: "",
        order_shop_ids: [],
        period: ["", ""],
        status: true,
        suppliers_enabled: false,
        parameters: "",
    });

    const getSupplierPrefix = (supplier) => {
        const name = (supplier.name || supplier.title || "").toLowerCase();
        const label = (supplier.title || "").toLowerCase();
        if (/^vv_|^tch_/.test(name) && !/vlantana/i.test(label)) {
            return "VV";
        }
        return "BB";
    };

    const fetchSuppliers = async (search = "") => {
        setSuppliersLoading(true);
        try {
            const params = new URLSearchParams();
            params.append("page", 1);
            params.append("per_page", 20);
            if (search) {
                params.append("q[title_or_code_cont]", search);
            }
            const response = await axios.get(`/api/v1/suppliers?${params.toString()}`);
            const suppliersData = response.data?.data || response.data || [];
            setSuppliers(
                suppliersData.map((supplier) => {
                    const prefix = getSupplierPrefix(supplier);
                    const baseLabel = supplier.title || supplier.code || `Supplier ${supplier.id}`;
                    return {
                        value: supplier.id,
                        label: `${prefix} - ${baseLabel}`,
                        code: supplier.code || "",
                        name: supplier.name || "",
                        prefix: prefix,
                    };
                })
            );
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        } finally {
            setSuppliersLoading(false);
        }
    };

    const fetchOrderShops = async () => {
        setOrderShopsLoading(true);
        try {
            const response = await axios.get("/api/v1/order_shops");
            const shopsData = response.data?.shops || response.data?.data || response.data || [];
            setOrderShops(
                shopsData.map((shop) => ({
                    value: shop.value || shop.id,
                    label: shop.label || shop.title || shop.identifier || `Shop ${shop.id}`,
                }))
            );
        } catch (error) {
            console.error("Error fetching order shops:", error);
        } finally {
            setOrderShopsLoading(false);
        }
    };

    const parseCommentStateIds = (value) => {
        if (!Array.isArray(value)) return [];
        return value
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id) && id > 0);
    };

    const fetchCommentStates = async () => {
        setCommentStatesLoading(true);
        try {
            const response = await axios.get("/api/v1/comment_states?page=1&per_page=99999");
            const statesData = response.data?.data || response.data || [];
            setCommentStates(
                statesData.map((state) => ({
                    value: state.id,
                    label: state.comment_type
                        ? `${state.name} (${state.comment_type.replace("comment_", "")})`
                        : state.name,
                }))
            );
        } catch (error) {
            console.error("Error fetching comment states:", error);
            setCommentStates([]);
        } finally {
            setCommentStatesLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppliers();
        fetchOrderShops();
        fetchCommentStates();
    }, []);

    useEffect(() => {
        if (!creating && id) {
            fetchSystemRule();
        }
    }, [id, creating]);

    const fetchSystemRule = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/v1/system_rules/${id}`);
            const ruleData = response.data;

            let parsedParameters = {};
            try {
                parsedParameters = ruleData.parameters ? JSON.parse(ruleData.parameters) : {};
            } catch (e) {
                console.error("Error parsing parameters:", e);
            }

            setValue({
                id: ruleData.id,
                rule_title: ruleData.rule_title || "",
                rule_code: ruleData.rule_code || "",
                order_shop_ids: ruleData.order_shops?.map((shop) => shop.id) || [],
                period: ruleData.period || ["", ""],
                status: ruleData.status ?? true,
                parameters: ruleData.parameters || "",
                suppliers_enabled: ruleData.suppliers_enabled || false,
            });

            if (parsedParameters.suppliers && Array.isArray(parsedParameters.suppliers)) {
                setSelectedShops(parsedParameters.suppliers.map((supplier) => ({
                    ...supplier,
                    prefix: getSupplierPrefix({ code: supplier.code, title: supplier.label }),
                })));
            }

            setBlacklistParams({
                canceled_orders_limit: Number(parsedParameters.canceled_orders_limit) || 0,
                returned_orders_limit: Number(parsedParameters.returned_orders_limit) || 0,
                canceled_comment_state_ids: parseCommentStateIds(
                    parsedParameters.canceled_comment_state_ids || parsedParameters.canceled_comment_ids
                ),
                returned_comment_state_ids: parseCommentStateIds(
                    parsedParameters.returned_comment_state_ids || parsedParameters.returned_comment_ids
                ),
            });

            if (ruleData.order_shops) {
                setSelectedOrderShops(ruleData.order_shops.map((shop) => shop.id));
                setOriginalOrderShops(ruleData.order_shops.map((shop) => ({
                    id: shop.system_rule_order_shop_id || shop.pivot_id,
                    order_shop_id: shop.id,
                })).filter((item) => item.id));
            }

            if (ruleData.system_rule_order_shops) {
                setOriginalOrderShops(ruleData.system_rule_order_shops.map((sros) => ({
                    id: sros.id,
                    order_shop_id: sros.order_shop_id,
                })));
            }
        } catch (error) {
            console.error("Error fetching system rule:", error);
            antdMessageError("Failed to load system rule");
            modalFunc(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchSuppliers(searchText);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchText]);

    if (loading) {
        return (
            <ModalSpecial
                style={{
                    width: 500,
                }}
                onClose={() => modalFunc(false)}
            >
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "100%" }}
                >
                    <Spinner />
                </div>
            </ModalSpecial>
        );
    }

    const onChange = (e) => {
        const { name, value: val } = e.target;
        setValue((prev) => ({
            ...prev,
            [name]: val,
        }));
    };

    const validError = () => {
        let error = {};
        if (!value.rule_title) error.rule_title = "Rule title is required";
        if (!value.rule_code) error.rule_code = "Please select a rule type";
        if (selectedOrderShops.length === 0) {
            error.order_shop_ids = "Please select at least one order shop";
        }
        if (!value.period || !value.period[0]) {
            error.period = "Period is required";
        }

        if (value.rule_code === "blacklist_client") {
            const canceledLimit = Number(blacklistParams.canceled_orders_limit) || 0;
            const returnedLimit = Number(blacklistParams.returned_orders_limit) || 0;

            if (canceledLimit > 0 && blacklistParams.canceled_comment_state_ids.length === 0) {
                error.canceled_comment_state_ids = "Please select at least one canceled comment";
            }

            if (returnedLimit > 0 && blacklistParams.returned_comment_state_ids.length === 0) {
                error.returned_comment_state_ids = "Please select at least one returned comment";
            }
        }

        setErrors(error);
        return Object.values(error).length > 0;
    };

    const onSave = async () => {
        try {
            setErrors({});
            if (validError()) return;

            setSaving(true);

            const parametersData = {};

            if (value.rule_code === "warehouse_priority") {
                parametersData.suppliers = selectedShops.map((shop) => ({
                    value: shop.value,
                    label: shop.label,
                    code: shop.code || "",
                    name: shop.name,
                    quantity: shop.quantity || 0,
                }));
            }

            if (value.rule_code === "blacklist_client") {
                parametersData.canceled_orders_limit = Number(blacklistParams.canceled_orders_limit) || 0;
                parametersData.returned_orders_limit = Number(blacklistParams.returned_orders_limit) || 0;
                parametersData.canceled_comment_state_ids = parseCommentStateIds(
                    blacklistParams.canceled_comment_state_ids
                );
                parametersData.returned_comment_state_ids = parseCommentStateIds(
                    blacklistParams.returned_comment_state_ids
                );
            }

            const buildOrderShopsAttributes = () => {
                const attributes = [];

                selectedOrderShops.forEach((shopId) => {
                    const existing = originalOrderShops.find((o) => o.order_shop_id === shopId);
                    if (existing) {
                        attributes.push({
                            id: existing.id,
                            order_shop_id: shopId,
                            _destroy: false,
                        });
                    } else {
                        attributes.push({
                            order_shop_id: shopId,
                            _destroy: false,
                        });
                    }
                });

                originalOrderShops.forEach((original) => {
                    if (!selectedOrderShops.includes(original.order_shop_id)) {
                        attributes.push({
                            id: original.id,
                            _destroy: true,
                        });
                    }
                });

                return attributes;
            };

            const payload = {
                system_rule: {
                    rule_title: value.rule_title,
                    rule_code: value.rule_code,
                    period: value.period,
                    status: value.status,
                    parameters: JSON.stringify(parametersData),
                    system_rule_order_shops_attributes: buildOrderShopsAttributes(),
                    suppliers_enabled: value.suppliers_enabled,
                },
            };

            if (creating) {
                await axios.post("/api/v1/system_rules", payload);
                antdMessageSuccess("System rule created successfully");
            } else {
                await axios.put(`/api/v1/system_rules/${id}`, payload);
                antdMessageSuccess("System rule updated successfully");
            }

            if (onSaveSuccess) {
                onSaveSuccess();
            }
            modalFunc(false);
        } catch (e) {
            console.error("Error saving system rule:", e);
            antdMessageError(e.response?.data?.message || "Failed to save system rule");
        } finally {
            setSaving(false);
        }
    };

    const onDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) {
            return;
        }

        const newShops = Array.from(selectedShops);
        const [removed] = newShops.splice(source.index, 1);
        newShops.splice(destination.index, 0, removed);

        setSelectedShops(newShops);

        const shopIds = newShops.map(shop => shop.value);
        onChange({
            target: {
                name: "order_shop_ids",
                value: shopIds,
            },
        });
    };

    const handleAddShop = (shopId) => {
        const shop = suppliers.find(s => s.value === shopId);
        if (shop && !selectedShops.find(s => s.value === shopId)) {
            const newShops = [...selectedShops, { ...shop, quantity: 0 }];
            setSelectedShops(newShops);

            const shopIds = newShops.map(s => s.value);
            onChange({
                target: {
                    name: "order_shop_ids",
                    value: shopIds,
                },
            });
        }
        setSearchText("");
    };

    const handleRemoveShop = (shopId) => {
        const newShops = selectedShops.filter(s => s.value !== shopId);
        setSelectedShops(newShops);

        const shopIds = newShops.map(s => s.value);
        onChange({
            target: {
                name: "order_shop_ids",
                value: shopIds,
            },
        });
    };

    const handleQuantityChange = (shopId, quantity) => {
        setSelectedShops(prevShops =>
            prevShops.map(shop =>
                shop.value === shopId ? { ...shop, quantity } : shop
            )
        );
    };

    return (
        <ModalSpecial
            style={{
                width: 500,
            }}
            text={creating ? "Create rule" : "Edit rule"}
            maskClosable
            onClose={() => {
                if (confirm(`Are you sure want to close modal?`)) {
                    return modalFunc(false);
                }
            }}
        >
            <div>
                <TextInputModal
                    text="Rule title"
                    name="rule_title"
                    required={true}
                    value={value.rule_title}
                    onChange={(e) => onChange(e)}
                    warning={errors.rule_title}
                />

                <div className="my-2" />

                <div>
                    <Label required={true}>System rule base</Label>
                    {errors.rule_code && (
                        <div className="text-danger text-xs">{errors.rule_code}</div>
                    )}
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select rule type"
                        value={value.rule_code || undefined}
                        onChange={(val) =>
                            onChange({
                                target: { name: "rule_code", value: val },
                            })
                        }
                        options={rules.map((rule) => ({
                            label: rule.title,
                            value: rule.code,
                        }))}
                    />
                </div>

                <div className="my-3" />

                <div>
                    <Label required={true}>Order Shops</Label>
                    {errors.order_shop_ids && (
                        <div className="text-danger text-xs">{errors.order_shop_ids}</div>
                    )}
                    <Select
                        mode="multiple"
                        showSearch
                        placeholder="Select order shops"
                        style={{ width: "100%" }}
                        value={selectedOrderShops}
                        onChange={setSelectedOrderShops}
                        loading={orderShopsLoading}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={orderShops}
                    />
                </div>

                <div className="my-3" />

                {value.rule_code === "warehouse_priority" && (
                    <div>
                        <Label>Warehouse autosys control (sorted by priority)</Label>
                        {errors.supplier_ids && (
                            <div className="text-danger text-xs">{errors.supplier_ids}</div>
                        )}

                        <Select
                            showSearch
                            placeholder="Search and select supplier to add"
                            style={{ width: "100%", marginBottom: 12 }}
                            onChange={handleAddShop}
                            onSearch={setSearchText}
                            searchValue={searchText}
                            value={null}
                            loading={suppliersLoading}
                            filterOption={false}
                            notFoundContent={suppliersLoading ? <Spinner /> : "No suppliers found"}
                            options={suppliers.filter(supplier => !selectedShops.find(s => s.value === supplier.value))}
                        />

                        {selectedShops.length === 0 && (
                            <div className="text-sm text-gray-500 mb-2">
                                No suppliers selected
                            </div>
                        )}

                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="shops">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                    >
                                        {selectedShops.map((shop, index) => (
                                            <Draggable
                                                key={shop.value.toString()}
                                                draggableId={shop.value.toString()}
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <div
                                                            className="d-flex align-items-center justify-content-between bg-white p-2 mb-2"
                                                            style={{
                                                                border: "1px solid #E9E9E9",
                                                                borderRadius: 4,
                                                                backgroundColor: "#FCFCFC",
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center">
                                                                <div className="mr-2">
                                                                    <IconDraggableBig />
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <span className="text-xs text-muted mr-2" style={{ minWidth: 20 }}>
                                                                        {index + 1}.
                                                                    </span>
                                                                    {shop.prefix && (
                                                                        <span
                                                                            className="text-xs mr-2"
                                                                            style={{
                                                                                backgroundColor: shop.prefix === "VV" ? "#e6f7ff" : "#e6f7ff",
                                                                                color: shop.prefix === "VV" ? "#1890ff" : "#1890ff",
                                                                                padding: "2px 6px",
                                                                                borderRadius: 4,
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {shop.prefix}
                                                                        </span>
                                                                    )}
                                                                    <span className="text-sm text-black">
                                                                        {shop.label?.replace(/^(VV|BB) - /, '') || shop.label}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={shop.quantity || 0}
                                                                    onChange={(e) => handleQuantityChange(shop.value, parseInt(e.target.value) || 0)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{
                                                                        width: 60,
                                                                        padding: "4px 8px",
                                                                        border: "1px solid #d9d9d9",
                                                                        borderRadius: 4,
                                                                        marginRight: 8,
                                                                        textAlign: "center",
                                                                    }}
                                                                />
                                                                <ButtonDelete
                                                                    onDelete={() => handleRemoveShop(shop.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                )}

                {value.rule_code === "blacklist_client" && (
                    <div>
                        <div
                            style={{
                                backgroundColor: "#F5F5F5",
                                borderRadius: 4,
                                padding: 10,
                            }}
                        >
                            <div style={{ display: "flex", gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <Label>Canceled orders limit:</Label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={blacklistParams.canceled_orders_limit}
                                            onChange={(e) => {
                                                const nextValue = Number(e.target.value) || 0;
                                                setBlacklistParams((prev) => ({
                                                    ...prev,
                                                    canceled_orders_limit: nextValue,
                                                }));
                                            }}
                                            style={{
                                                width: "30%",
                                                padding: "6px 10px",
                                                border: "1px solid #d9d9d9",
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginTop: 8 }}>
                                        <Label>Canceled comments:</Label>
                                        {errors.canceled_comment_state_ids && (
                                            <div className="text-danger text-xs">{errors.canceled_comment_state_ids}</div>
                                        )}
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            placeholder="Select comments"
                                            style={{ width: "100%" }}
                                            maxTagCount="responsive"
                                            maxTagTextLength={20}
                                            value={blacklistParams.canceled_comment_state_ids}
                                            onChange={(selected) => {
                                                setBlacklistParams((prev) => ({
                                                    ...prev,
                                                    canceled_comment_state_ids: selected,
                                                }));
                                            }}
                                            loading={commentStatesLoading}
                                            optionFilterProp="label"
                                            options={commentStates}
                                        />
                                    </div>
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <Label>Returned orders limit:</Label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={blacklistParams.returned_orders_limit}
                                            onChange={(e) => {
                                                const nextValue = Number(e.target.value) || 0;
                                                setBlacklistParams((prev) => ({
                                                    ...prev,
                                                    returned_orders_limit: nextValue,
                                                }));
                                            }}
                                            style={{
                                                width: "30%",
                                                padding: "6px 10px",
                                                border: "1px solid #d9d9d9",
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginTop: 8 }}>
                                        <Label>Returned comments:</Label>
                                        {errors.returned_comment_state_ids && (
                                            <div className="text-danger text-xs">{errors.returned_comment_state_ids}</div>
                                        )}
                                        <Select
                                            mode="multiple"
                                            showSearch
                                            placeholder="Select comments"
                                            style={{ width: "100%" }}
                                            maxTagCount="responsive"
                                            maxTagTextLength={20}
                                            value={blacklistParams.returned_comment_state_ids}
                                            onChange={(selected) => {
                                                setBlacklistParams((prev) => ({
                                                    ...prev,
                                                    returned_comment_state_ids: selected,
                                                }));
                                            }}
                                            loading={commentStatesLoading}
                                            optionFilterProp="label"
                                            options={commentStates}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: 8, fontSize: 12, color: "#666", fontWeight: 600 }}>
                                *In row
                            </div>
                        </div>
                    </div>
                )}

                <div className="my-3" />

                <div>
                    <RangePickerComp
                        renderExtraFooter={() => (
                            <div className="d-flex align-items-center justify-content-center">
                                {!!value.period &&
                                    !!value.period.length &&
                                    !!value.period[0] && (
                                        <div
                                            className="text-primary py-2 cursor-pointer"
                                            onClick={() => {
                                                onChange({
                                                    target: {
                                                        name: "period",
                                                        value: [value.period[0], ""],
                                                    },
                                                });
                                            }}
                                        >
                                            Do not set end date
                                        </div>
                                    )}
                            </div>
                        )}
                        style={{
                            width: "100%",
                        }}
                        label="Period"
                        required={true}
                        placeholder={["Start date", "End date"]}
                        disabledDate={() => { }}
                        value={
                            value.period
                                ? [
                                    getDateFormat(value.period[0]),
                                    getDateFormat(value.period[1]),
                                ]
                                : ["", ""]
                        }
                        onCalendarChange={(_, val) => {
                            onChange({
                                target: {
                                    name: "period",
                                    value: val,
                                },
                            });
                        }}
                        warning={errors.period}
                    />
                </div>

                <div className="my-3" />
                {value.rule_code === "auto_shipment" && (
                    <div className="d-flex flex-column">
                        <SwitchComp
                            placeholder="Enabled for suppliers"
                            checked={value.suppliers_enabled}
                            onChange={() =>
                                onChange({ target: { name: "suppliers_enabled", value: !value.suppliers_enabled } })
                            }
                        />
                    </div>
                )}

                <div className="d-flex flex-column">
                    <SwitchComp
                        placeholder="Status"
                        checked={value.status}
                        onChange={() =>
                            onChange({ target: { name: "status", value: !value.status } })
                        }
                    />
                </div>

                <div className="my-3" />

                <ButtonSecondary onClick={() => onSave()} disabled={saving}>
                    {saving ? "Saving..." : creating ? "Create" : "Update"}
                </ButtonSecondary>
            </div>
        </ModalSpecial>
    );
};

export default SystemRulesModal;
